import { T } from '@/design/tokens';

/**
 * Cartões de resumo do topo da tela.
 *
 * Regra que não se negocia: cartão mostra DADO REAL, vindo do mesmo filtro da
 * lista. Um número decorativo no topo é pior que nenhum — o operador decide
 * olhando para ele.
 *
 * Sem ícone: um ícone colorido num quadradinho em cada cartão é o tique de
 * painel gerado que o reboot tirou do sistema todo. O rótulo diz o que é.
 * Mesmo desenho da faixa de indicadores das outras telas (`.metrics`):
 * rótulo em cima, número embaixo, cartões colados numa faixa só.
 * Cor só no NÚMERO, e só quando ele pede atenção — atenção ou problema.
 * "Entregues" em verde e "Em aberto" em azul pintavam a faixa inteira.
 */
export type SummaryTone = 'neutro' | 'info' | 'ok' | 'atencao' | 'ruim';

const COR_DO_NUMERO: Record<SummaryTone, string | undefined> = {
  neutro: undefined,
  info: undefined,
  ok: undefined,
  atencao: T.amber,
  ruim: T.red,
};

export interface SummaryCard {
  key: string;
  label: string;
  value: string;
  hint?: string;
  tone?: SummaryTone;
  /** Clicar aplica o filtro correspondente na lista. */
  onClick?: () => void;
  active?: boolean;
  /**
   * Contador que fica CINZA quando é zero.
   *
   * "0 com divergência" pintado de vermelho grita o mesmo que "4 com
   * divergência" — e quem vê cinco cartões gritando junto para de olhar para
   * todos. Zero é a boa notícia: fica quieto. O tom só acende quando há o que
   * resolver. Não vale para dinheiro: R$ 0,00 no período é informação, não
   * ausência de problema.
   */
  apagarSeZero?: boolean;
}

export function SummaryCards({ cards, loading }: { cards: SummaryCard[]; loading?: boolean }) {
  return (
    <div className="summary">
      {cards.map((card) => {
        const zerado = card.apagarSeZero === true && Number(card.value.replace(/\D/g, '')) === 0;
        const cor = COR_DO_NUMERO[zerado ? 'neutro' : (card.tone ?? 'neutro')];
        const conteudo = (
          <>
            <span className="summary__label">{card.label}</span>
            <span className="summary__value" style={cor ? { color: cor } : undefined}>
              {loading ? '—' : card.value}
            </span>
            {card.hint && <span className="summary__hint">{card.hint}</span>}
          </>
        );

        return card.onClick ? (
          <button
            key={card.key}
            type="button"
            className={`summary__card summary__card--acionavel${card.active ? ' is-active' : ''}`}
            onClick={card.onClick}
            aria-pressed={card.active ?? false}
          >
            {conteudo}
          </button>
        ) : (
          <div key={card.key} className="summary__card">
            {conteudo}
          </div>
        );
      })}
    </div>
  );
}
